<template>
  <div>
    <!-- "All" exists only here: the dashboard's job is the whole-platform
         view, so the combined total stays one click away. Every other screen
         picks one product outright. -->
    <!-- Narrowed to the tab this user is allowed. This screen honoured no
         locks at all before: its counts went in raw, so a Dashboard product
         lock was applied to the data and invisible in the tab bar. -->
    <!-- Refresh rides on this row rather than in a PageHeader above it, for the
         same reason as Products' Add product: one button does not earn a band
         of its own, and this row has the width spare. -->
    <ProductSwitch
      :value="product"
      :counts="visibleProductCounts"
      @change="selectProduct"
    >
      <template #action>
        <v-btn small outlined class="text-none" :loading="loading" @click="load">
          <LucideIcon name="refresh-cw" :size="18" class="mr-1" />
          Refresh
        </v-btn>
      </template>
    </ProductSwitch>

    <FilterBar
      :date-range="dateRange"
      :show-search="false"
      @update:dateRange="onDateRange"
      @reset="resetFilters"
    />

    <ErrorState v-if="error" :message="error" @retry="load" />

    <template v-else>
      <!-- KPI row -->
      <v-row dense class="mb-1">
        <v-col
          v-for="kpi in kpis"
          :key="kpi.key"
          cols="6"
          sm="4"
          md="3"
          lg="3"
        >
          <KpiCard
            :label="kpi.label"
            :value="kpi.value"
            :icon="kpi.icon"
            :color="kpi.color"
            :sub="kpi.sub || ''"
            :is-currency="!!kpi.is_currency"
            :loading="loading"
            clickable
            @click="drillDown(kpi.key)"
          />
        </v-col>
      </v-row>

      <!-- Conversion ratios. Directly under the KPI row because they are that
           row's numbers divided by each other -- reading them next to their
           own inputs is what makes them checkable. -->
      <div class="kf-card neumorphism-flat mb-1">
        <div class="kf-ratios__head">
          <div>
            <div class="kf-card__title">Conversion ratios</div>
            <div class="kf-card__hint">{{ ratioHint }}</div>
          </div>
          <div class="kf-ratio-toggle">
            <button
              v-for="opt in ratioViews"
              :key="opt.value"
              class="kf-ratio-btn"
              :class="{ 'kf-ratio-btn--active': ratioView === opt.value }"
              @click="ratioView = opt.value"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>

        <!-- Not clickable, unlike the tiles above: a ratio has no one screen to
             drill into, and a tile that looks pressable and does nothing is
             worse than one that plainly reports. -->
        <v-row dense>
          <v-col v-for="r in activeRatios" :key="r.key" cols="12" sm="4">
            <KpiCard
              :label="r.label"
              :value="r.value"
              is-percent
              :sub="r.sub"
              :icon="r.icon"
              :color="r.color"
              :loading="loading"
            />
          </v-col>
        </v-row>
      </div>

      <v-row dense>
        <!-- Funnel -->
        <v-col cols="12" md="6">
          <div class="kf-card neumorphism-flat">
            <div class="kf-card__title">Conversion funnel</div>
            <div class="kf-card__hint">
              Distinct users who reached at least each stage, so disbursed users
              are also counted as having been in process.
            </div>

            <div v-if="loading" class="pt-3">
              <v-skeleton-loader type="list-item-two-line" v-for="n in 5" :key="n" />
            </div>

            <EmptyState
              v-else-if="!funnel.length"
              title="No funnel data"
              message="No users signed up inside this date range."
              icon="list-filter"
            />

            <div v-else class="kf-funnel">
              <div v-for="(stage, i) in funnel" :key="stage.label" class="kf-funnel__row">
                <div class="kf-funnel__meta">
                  <span class="kf-funnel__label">{{ stage.label }}</span>
                  <span class="kf-funnel__value">
                    {{ fmt.number(stage.value) }}
                    <span v-if="i > 0" class="kf-funnel__pct">
                      {{ stepRate(i) }}
                    </span>
                  </span>
                </div>
                <div class="kf-funnel__bar">
                  <div
                    class="kf-funnel__fill"
                    :style="{
                      width: barWidth(stage.value),
                      backgroundColor: stage.color,
                    }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </v-col>

        <!-- Trend -->
        <v-col cols="12" md="6">
          <div class="kf-card neumorphism-flat">
            <div class="kf-card__title">Clicks &amp; disbursals</div>
            <div class="kf-card__hint">Last 12 weeks.</div>

            <v-skeleton-loader v-if="loading" type="image" class="mt-3" />
            <apexchart
              v-else
              type="area"
              height="248"
              :options="trendOptions"
              :series="trendSeries"
            />
          </div>
        </v-col>
      </v-row>

      <!-- Recent activity -->
      <!-- No @row-click. Rows used to open the Lead detail screen, which is a
           user profile: this table is live (LIVE_MODULES.dashboard) so its
           user_id values are real, but getLeadDetail is still on mock data and
           has no record with those ids, so every tap landed on "Could not load
           this". The row carries the customer, offer, product and status
           already, so there is nothing behind the tap worth restoring. -->
      <DataTable
        class="mt-1"
        title="Recent offer clicks"
        :headers="headers"
        :items="recent"
        :loading="loading"
        :total-count="recent.length"
        empty-title="No recent clicks"
        empty-message="No offers were clicked in this date range."
        empty-icon="mouse-pointer-click"
      >
        <template #actions>
          <v-btn small text class="text-none" :to="{ name: 'karrofin-users' }">
            View all users
            <LucideIcon name="arrow-right" :size="18" />
          </v-btn>
        </template>

        <template #[`item.name`]="{ item }">
          <div class="kf-user">
            <span class="kf-user__avatar">{{ fmt.initials(item.name) }}</span>
            <div>
              <div class="kf-user__name">{{ item.name }}</div>
              <div class="kf-user__sub">{{ fmt.mobile(item.mobile) }}</div>
            </div>
          </div>
        </template>

        <template #[`item.status`]="{ item }">
          <StatusChip :status="item.status" small />
        </template>

        <template #[`item.at`]="{ item }">
          <span :title="fmt.dateTime(item.at)">{{ fmt.relative(item.at) }}</span>
        </template>
      </DataTable>
    </template>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import KpiCard from "@/components/karrofin/KpiCard.vue";
import FilterBar from "@/components/karrofin/FilterBar.vue";
import ProductSwitch from "@/components/karrofin/ProductSwitch.vue";
import DataTable from "@/components/karrofin/DataTable.vue";
import StatusChip from "@/components/karrofin/StatusChip.vue";
import EmptyState from "@/components/karrofin/EmptyState.vue";
import ErrorState from "@/components/karrofin/ErrorState.vue";
import api from "@/app-js/karrofin/api";
import store, {
  defaultDateRange,
  productFromQuery,
} from "@/app-js/karrofin/store";
import fmt from "@/app-js/karrofin/format";
import {
  PRODUCT_FILTER_ALL,
  PRODUCT_TYPES,
  RATIO_VIEWS,
} from "@/app-js/karrofin/constants";

// The only screen that offers a combined view, so the only one whose allowed
// list starts with "All" -- which also makes it the default here.
const DASHBOARD_PRODUCTS = [PRODUCT_FILTER_ALL].concat(PRODUCT_TYPES);

export default {
  name: "KarroFinDashboard",
  components: {
    ProductSwitch,
    apexchart: VueApexCharts,
    KpiCard,
    FilterBar,
    DataTable,
    StatusChip,
    EmptyState,
    ErrorState,
  },
  data() {
    return {
      fmt,
      loading: true,
      error: null,
      product: productFromQuery(this.$route.query.product, DASHBOARD_PRODUCTS),
      productCounts: DASHBOARD_PRODUCTS.map((label) => ({ label, count: 0 })),
      kpis: [],
      ratioViews: RATIO_VIEWS,
      /* Lead by default: the lead ratios are the ones ops act on. Local state,
         not the URL or the shared store -- the dashboard only persists
         `product` that way, and a ratio view is a glance, not somewhere you
         send a colleague. */
      ratioView: "lead",
      ratios: { offer: [], lead: [] },
      funnel: [],
      trend: { categories: [], series: [] },
      recent: [],
      headers: [
        { text: "Customer", value: "name" },
        { text: "Offer", value: "title" },
        { text: "Product", value: "product_type" },
        { text: "Status", value: "status" },
        { text: "Clicked", value: "at" },
      ],
    };
  },
  computed: {
    /* Only the product tabs this user is allowed. Unlocked, every tab -- so
       nothing changes for the admin or an unrestricted member. */
    visibleProductCounts() {
      return this.allowedSegments(
        "dashboard", "product_type", this.productCounts, "label");
    },
    /* The shared range can be [] now -- Offer Management and User Management
       let it be cleared to "all dates". This screen has never run without a
       range, so it keeps reading the default when the shared one is empty. */
    dateRange() {
      const range = store.dateRange;
      return range && range.length === 2 ? range : defaultDateRange();
    },
    /** "All" is a UI-only value; the query layer wants null for "everything". */
    productParam() {
      return this.product === PRODUCT_FILTER_ALL ? null : this.product;
    },
    /**
     * The three ratios for the selected half of the toggle, ready to render.
     *
     * The percentage is derived here rather than in the query layer so the
     * zero-denominator case is decided once: a null value, which KpiCard's
     * percent mode renders as "-". A range with no offers must not read
     * "0.0%" -- that claims nothing converted, when in fact nothing was
     * offered.
     */
    activeRatios() {
      return (this.ratios[this.ratioView] || []).map((r) => ({
        ...r,
        value: r.whole ? r.part / r.whole : null,
        sub: r.whole
          ? `${fmt.number(r.part)} of ${fmt.number(r.whole)} ${r.whole_label}`
          : `No ${r.whole_label} in this range`,
      }));
    },
    /* The lead line is load-bearing: "lead" on this panel's own Leads screen
       means a customer, and here it means one clicked offer. */
    ratioHint() {
      return this.ratioView === "lead"
        ? "A lead is an offer a customer clicked."
        : "Out of every offer issued in this range.";
    },
    maxFunnel() {
      return this.funnel.reduce((m, s) => Math.max(m, s.value), 0);
    },
    trendSeries() {
      return [
        { name: "Clicks", data: this.trend.series.map((s) => s.clicks) },
        { name: "Disbursed", data: this.trend.series.map((s) => s.disbursed) },
      ];
    },
    trendOptions() {
      return {
        chart: { type: "area", toolbar: { show: false }, fontFamily: "inherit" },
        colors: ["#00796B", "#2E7D32"],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        fill: {
          type: "gradient",
          gradient: { opacityFrom: 0.35, opacityTo: 0.05 },
        },
        xaxis: {
          categories: this.trend.categories,
          labels: { style: { fontSize: "10px", colors: "#54636F" } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: { labels: { style: { fontSize: "10px", colors: "#54636F" } } },
        grid: { borderColor: "#DBE6F0", strokeDashArray: 4 },
        legend: { position: "top", horizontalAlign: "right", fontSize: "11px" },
      };
    },
  },
  created() {
    /* Land a pinned member on the product they are allowed. This screen's
       default is "All", which a pinned member is not entitled to. */
    const pinned = this.lockedValues("dashboard", "product_type");
    if (pinned && pinned.indexOf(this.product) === -1) {
      this.product = pinned[0];
    }
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.error = null;
      api
        .getDashboard({
          product_type: this.productParam,
          date_range: this.dateRange,
        })
        .then((data) => {
          this.kpis = data.kpis;
          this.ratios = data.ratios || { offer: [], lead: [] };
          this.funnel = data.funnel;
          this.trend = data.trend;
          this.recent = data.recent;
          this.productCounts = data.product_counts || this.productCounts;
        })
        .catch((err) => {
          this.error = err;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onDateRange(range) {
      // Clearing the box here still means "back to the default", as before.
      store.setDateRange(range && range.length === 2 ? range : defaultDateRange());
      this.load();
    },
    resetFilters() {
      store.setDateRange(defaultDateRange());
      this.load();
    },
    barWidth(value) {
      if (!this.maxFunnel) return "0%";
      return Math.max(2, (value / this.maxFunnel) * 100) + "%";
    },
    stepRate(i) {
      const prev = this.funnel[i - 1];
      if (!prev || !prev.value) return "";
      return "· " + fmt.rate(this.funnel[i].value, prev.value);
    },
    selectProduct(product) {
      this.product = product;
      this.load();
    },
    // KPI tiles are shortcuts into the screen that explains them.
    drillDown(key) {
      const map = {
        leads: { name: "karrofin-users" },
        // Offer Management, NOT the Products catalogue. This tile counts the
        // individual offers issued to customers (hundreds of them); Products
        // lists the ~11 product definitions. Different unit, different screen --
        // and Products ignores the query string entirely, so the `product`
        // guard below was silently doing nothing on this one.
        offers: { name: "karrofin-applications" },
        // Unfiltered on purpose. No single stage means "clicked": pinning
        // "Some offers clicked" hid the customers who clicked ALL of theirs, and
        // everyone who clicked and has since moved to Under Process, Disbursed
        // or Rejected -- all of whose clicks are in this tile's number. The
        // pipeline tabs on the target carry the per-stage counts instead.
        clicks: { name: "karrofin-applications" },
        ec1: { name: "karrofin-users", query: { stage: "EC1 Pending" } },
        under_process: { name: "karrofin-applications", query: { stage: "Under Process" } },
        disbursed: { name: "karrofin-applications", query: { stage: "Disbursed" } },
        rejected: { name: "karrofin-applications", query: { stage: "Rejected" } },
        amount: { name: "karrofin-applications", query: { stage: "Disbursed" } },
      };
      const target = map[key];
      if (!target) return;

      // Leads and Offer Management hold their own product now, so it has to
      // travel in the URL or a drill-down from a Business Loan dashboard would
      // quietly land on a Personal Loan list. Nothing to send when the
      // dashboard is on "All" -- those screens have no such view.
      if (PRODUCT_TYPES.indexOf(this.product) !== -1) {
        target.query = { ...(target.query || {}), product: this.product };
      }
      this.$router.push(target).catch(() => {});
    },
  },
};
</script>

<style scoped>
.kf-card {
  background: var(--kf-surface);
  border-radius: 10px;
  padding: 14px 16px;
  height: 100%;
}
.kf-card__title {
  font-size: 15px;
  font-weight: 700;
  color: var(--kf-brand);
}
/* Only difference from the shared .kf-card__hint in kf-tokens.css: this one
   sits directly above the funnel bars and needs a little air under it. */
.kf-card__hint {
  margin-bottom: 6px;
}
/* Title and hint on the left, the toggle pinned right. Wraps rather than
   squeezing the buttons on a narrow screen. */
.kf-ratios__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}
.kf-ratio-toggle {
  display: flex;
  gap: 7px;
  flex: 0 0 auto;
}
/* The .kf-channel treatment from the Notifications composer: the house pattern
   for a small two-way control inside a card. ProductSwitch is for "which book
   of business am I looking at" and StatStrip is for filtering a table; this is
   neither. */
.kf-ratio-btn {
  padding: 6px 16px;
  border-radius: 8px;
  border: 1px solid var(--kf-border);
  background: var(--kf-surface-2);
  font-size: var(--kf-fs-meta);
  font-weight: 600;
  color: #000;
  cursor: pointer;
  outline: none;
  transition: all 0.15s ease;
}
/* The specificity trap this pattern always carries: `:hover` (0,2,0) outranks
   `--active` (0,1,0), so without the :not() the selected button greys out --
   white text on the hover background -- the moment the pointer touches it. */
.kf-ratio-btn:hover:not(.kf-ratio-btn--active) {
  background: var(--kf-hover);
  color: #000;
  border-color: var(--kf-border-strong);
}
.kf-ratio-btn--active {
  background: var(--kf-brand);
  border-color: var(--kf-brand);
  color: #fff;
}
.kf-ratio-btn--active:hover {
  background: var(--kf-brand-strong);
  border-color: var(--kf-brand-strong);
}
.kf-funnel {
  margin-top: 10px;
}
.kf-funnel__row {
  margin-bottom: 13px;
}
.kf-funnel__meta {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 5px;
}
.kf-funnel__label {
  font-size: var(--kf-fs-meta);
  color: #000;
  font-weight: 600;
}
.kf-funnel__value {
  font-size: var(--kf-fs-hint);
  font-weight: 700;
  color: var(--kf-brand);
}
.kf-funnel__pct {
  font-size: var(--kf-fs-label);
  font-weight: 600;
  color: #000;
  margin-left: 2px;
}
.kf-funnel__bar {
  height: 8px;
  border-radius: 6px;
  background: var(--kf-hover);
  overflow: hidden;
}
.kf-funnel__fill {
  height: 100%;
  border-radius: 6px;
  transition: width 0.4s ease;
}
.kf-user {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 4px 0;
}
.kf-user__avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--kf-brand-soft);
  color: var(--kf-brand);
  font-size: var(--kf-fs-label);
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kf-user__name {
  font-size: var(--kf-fs-hint);
  font-weight: 600;
  color: #000;
  line-height: 1.3;
}
.kf-user__sub {
  font-size: var(--kf-fs-label);
  color: #000;
}
</style>
